/**
 * Converts a relative or absolute image URL to use the API upload route
 * This ensures proper CORS headers are set
 * @param url - The image URL (can be relative or absolute)
 * @returns The absolute URL using the API upload route
 */
export const getImageUrl = (url: string | undefined | null): string => {
    if (!url) return '';

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.crm.we4task.com';

    // If already a full URL with http/https and pointing to API, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
        // If it's already pointing to the API base URL, return as is
        if (url.includes(API_BASE_URL)) {
            return url;
        }
        // Extract the path from absolute URL
        try {
            const urlObj = new URL(url);
            const pathname = urlObj.pathname;

            // Check if pathname contains /uploads/
            if (pathname.includes('/uploads/')) {
                // Extract the part starting from /uploads/
                const uploadsIndex = pathname.indexOf('/uploads/');
                url = pathname.substring(uploadsIndex);
            } else {
                // Not an uploads path, return as is
                return url;
            }
        } catch (e) {
            // Invalid URL, try to extract /uploads/ manually
            const uploadsIndex = url.indexOf('/uploads/');
            if (uploadsIndex !== -1) {
                url = url.substring(uploadsIndex);
            } else {
                return url;
            }
        }
    }

    // Handle relative URLs starting with /uploads/
    if (url.startsWith('/uploads/')) {
        const pathWithoutUploads = url.replace('/uploads/', '');
        const parts = pathWithoutUploads.split('/').filter(part => part.length > 0);

        if (parts.length >= 2) {
            const folder = parts[0];
            const filename = parts.slice(1).join('/');

            // Special handling for logos - use /api/upload/logo/:filename route
            if (folder === 'logos') {
                return `${API_BASE_URL}/api/upload/logo/${filename}`;
            }

            // For other folders, use /api/upload/:folder/:filename route
            return `${API_BASE_URL}/api/upload/${folder}/${filename}`;
        } else if (parts.length === 1) {
            // Edge case: /uploads/filename (no folder) - assume it's a logo
            return `${API_BASE_URL}/api/upload/logo/${parts[0]}`;
        }
    } else if (url.includes('/uploads/')) {
        // URL contains /uploads/ but doesn't start with it
        const uploadsIndex = url.indexOf('/uploads/');
        const uploadsPath = url.substring(uploadsIndex);
        const pathWithoutUploads = uploadsPath.replace('/uploads/', '');
        const parts = pathWithoutUploads.split('/').filter(part => part.length > 0);

        if (parts.length >= 2) {
            const folder = parts[0];
            const filename = parts.slice(1).join('/');

            // Special handling for logos
            if (folder === 'logos') {
                return `${API_BASE_URL}/api/upload/logo/${filename}`;
            }

            return `${API_BASE_URL}/api/upload/${folder}/${filename}`;
        } else if (parts.length === 1) {
            return `${API_BASE_URL}/api/upload/logo/${parts[0]}`;
        }
    }

    // Fallback - return original URL or construct API URL if it looks like a relative path
    if (url.startsWith('/')) {
        // It's a relative path, prepend API base URL
        return `${API_BASE_URL}${url}`;
    }

    return url;
};

/**
 * Format logo URL - alias for getImageUrl for backward compatibility
 * @param logoUrl - The logo URL
 * @returns The formatted logo URL with API base URL
 */
export const formatLogoUrl = (logoUrl: string | undefined | null): string => {
    return getImageUrl(logoUrl);
};

